/* eslint-disable max-len */
import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import ResizeObserver from 'react-resize-observer';
import Select from 'react-select';
import { useDebouncedCallback } from 'use-debounce';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';

import Text from '../../../../../shared/_TBComponents/Text';
import Toggle from '../../../../../shared/_TBComponents/Toggle';
import Tooltip from '../../../../../shared/_TBComponents/Tooltip';
import { updateOrganizationSettings } from '../../../../login/ducks/loginActions';
import {
  setModalOpen,
  setSupportPrefillSubject,
} from '../../../../../shared/components/Layout/ducks/layoutActions';
import timeZones from '../../../../../shared/json/timezones.json';
import { BoxShadow, ColorBlack, ColorGray } from '../../../../../shared/GlobalStyles';

const HeaderContainer = styled('div')`
  border-bottom: #e0e0e0 1px solid;
  margin-bottom: 15px;
  padding-bottom: 15px;
  padding-top: 15px;
`;

const FormFieldLabelContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const FormFieldInfoIcon = styled('div')`
  border-radius: 50%;
  border: 1px solid ${ColorGray};
  width: 18px;
  height: 18px;
  padding: 2px;
  margin-left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  a {
    display: flex;
    height: 12px !important;
    line-height: 12px !important;
  }
  @media screen and (max-width: 500px) {
    display: none;
  }
`;

const Row = styled('div')`
  align-items: center;
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  display: flex;
  flex-direction: row;
  ${({ height }) => height && `height: ${height};`}
  justify-content: space-between;
  margin-bottom: 15px;
`;

const SubText = styled('span')`
  color: ${(props) => props.theme.textColor};
  font-size: 16px;
  margin-top: 5px;
`;

const SupportLink = styled('span')`
  cursor: pointer;
  text-decoration: underline;
`;

const TextContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-right: 30px;
  max-width: 602px;
`;

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 45px;
  width: 100%;

  .react-toggle .react-toggle-track {
    background-color: #e9e9ea;
  }

  .react-toggle--checked .react-toggle-track {
    background-color: ${(props) => props.theme.colors.green};
  }

  .react-toggle-thumb {
    border: none;
  }

  .react-toggle:hover:not(.react-toggle--disabled) .react-toggle-track {
    background-color: #e9e9ea;
  }

  .react-toggle--checked:hover:not(.react-toggle--disabled)
    .react-toggle-track {
    background-color: ${(props) => props.theme.colors.green};
  }
`;

const OrganizationPreferences = ({ flags }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [containerWidth, setContainerWidth] = useState(null);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const organizationSettings = useSelector(
    (state) => state.auth.data.organizationSettings,
  );
  const subscriptionStatus = useSelector(
    (state) => state.sportsScience.data.subscriptionStatus,
  );

  const accountCode = currentUser?.accountCode;
  const accessLevel = currentUser.organizationAccessLevel;
  const currentGlobalTimeZone = organizationSettings?.generalSettings?.timeZone;
  const currentGlobalRepCap = organizationSettings?.generalSettings?.repCap;
  const currentGlobalWeightType = organizationSettings?.generalSettings?.weightType;
  const currnetGlobalMaxCalculation = organizationSettings?.generalSettings?.maxCalculation;
  const isAmsCoreSubscribed = subscriptionStatus?.packageSubscriptionStatus?.isSubscribed;
  const bodyHeatMapFlag = !!flags?.bodyHeatMaps;

  const handleOpenSupportModal = () => {
    dispatch(setSupportPrefillSubject(
      `Request weight unit change for ${currentUser?.organizationName}, account code ${currentUser?.organizationId}`,
    ));
    dispatch(setModalOpen('Support'));
  };

  const [
    organizationPreferencesSettings,
    setOrganizationPreferencesSettings,
  ] = useState({
    allowAthletesToResetPrograms: Boolean(
      organizationSettings?.dayBasedProgramSettings?.allowUserProgramRestarts,
    ),
    athleteBodyHeatMapEntry: Boolean(
      organizationSettings?.generalSettings?.athleteBodyHeatMapEntry,
    ),
    athleteManagementOfMaxes: Boolean(
      organizationSettings?.generalSettings?.athleteMaxPrManagement,
    ),
    dailyWorkoutEmails: Boolean(
      organizationSettings?.generalSettings?.dailyWorkoutEmailsEnabled,
    ),
    scheduledReportCardEmails: Boolean(
      organizationSettings?.generalSettings?.sendReportCards,
    ),
  });

  const timeZoneSelectionEntries = useMemo(
    () => Object.entries(timeZones).map((entry) => ({
      label: entry[1],
      value: entry[0],
    })),
    [],
  );

  const defaultTimezoneValue = useMemo(
    () => timeZoneSelectionEntries.filter(
      (entryObj) => entryObj.value === organizationSettings?.generalSettings?.timeZone,
    ),
    [timeZoneSelectionEntries, organizationSettings?.generalSettings?.timeZone],
  );

  const formFieldTextStyles = {
    fontSize: containerWidth && containerWidth < 375 ? 15 : 18,
    fontWeight: '700',
  };

  const hardCodedDefaultTimezoneValue = {
    label: '(GMT-05:00) Eastern Time (US & Canada)',
    value: 'America/New_York',
  };

  const weightTypeDefaultValue = organizationSettings?.generalSettings?.weightType === 1
    ? { label: 'kgs', value: 1 }
    : { label: 'lbs', value: 0 };

  const maxCalFormulatDefaultValue = organizationSettings?.generalSettings?.maxCalculation === 2
    ? { label: 'Epley', value: 2 }
    : { label: 'Brzycki', value: 1 };

  const repCapValue = {
    label: `${organizationSettings?.generalSettings?.repCap}`,
    value: organizationSettings?.generalSettings?.repCap,
  };

  const repsArray = useMemo(() => {
    const result = [];
    for (let i = 3; i <= 30; i += 1) {
      result.push({ label: i, value: i });
    }
    return result;
  }, []);

  const emailReceipeints = [
    { label: 'Athletes', value: 1 },
    { label: 'Parents/Guardians', value: 2 },
    { label: 'Athletes and Parents/Guardians', value: 3 },
  ];

  const reportCardAssignedCompletedOptions = [
    { label: 'Assigned', value: 1 },
    { label: 'Completed', value: 2 },
  ];

  const debouncedOrganizationPreferencesSettings = useDebouncedCallback(
    (newSettings) => {
      const valueObj = {
        dayBasedProgramSettings: {
          allowUserProgramRestarts: newSettings.allowAthletesToResetPrograms,
        },
        generalSettings: {
          athleteBodyHeatMapEntry: newSettings.athleteBodyHeatMapEntry,
          athleteMaxPrManagement: newSettings.athleteManagementOfMaxes,
          reportCardDateFilter: newSettings.reportCardDateFilter,
          dailyWorkoutEmailsEnabled: newSettings.dailyWorkoutEmails,
          reportCardRecipients: newSettings.reportCardRecipients,
          sendReportCards: newSettings.scheduledReportCardEmails,
        },
      };
      dispatch(updateOrganizationSettings(accountCode, valueObj));
    },
    1000,
  );

  const customSelectStyles = {
    container: (provided) => ({
      ...provided,
      minWidth: 120,
      height: 46,
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: '#E0E0E0',
      borderRadius: 6,
    }),
    menu: (provided) => ({
      ...provided,
      width: 200,
    }),
    control: (provided) => ({
      ...provided,
      minHeight: 46,
      width: '100%',
      border: 0,
      outline: '2px solid #E0E0E0',
    }),
  };

  const timeZoneCustomSelectStyles = useMemo(
    () => ({
      container: (provided) => ({
        ...provided,
        borderColor: '#E0E0E0',
        borderRadius: 6,
        borderStyle: 'solid',
        borderWidth: 1,
        height: 46,
        width: containerWidth && containerWidth < 375 ? 175 : 230,
      }),
      control: (provided) => ({
        ...provided,
        border: 0,
        minHeight: 46,
        outline: '2px solid #E0E0E0',
        width: '100%',
      }),
      menu: (provided) => ({
        ...provided,
        width: 200,
      }),
    }),
    [containerWidth],
  );

  const mapBackendValuesToSelectOptions = (backendValue, options) => {
    switch (backendValue) {
      case 'Athletes':
        return options.find((option) => option.label === 'Athletes');
      case 'Guardians':
        return options.find((option) => option.label === 'Parents/Guardians');
      case 'Athletes and Guardians':
        return options.find(
          (option) => option.label === 'Athletes and Parents/Guardians',
        );
      default:
        return null;
    }
  };

  const mapRecipientToInteger = (backendValue) => {
    switch (backendValue) {
      case 'Athletes':
        return 1;
      case 'Guardians':
        return 2;
      case 'Athletes and Guardians':
        return 3;
      default:
        return 1;
    }
  };

  const formFieldsConfig = useMemo(
    () => [
      {
        id: 1,
        defaultValue: defaultTimezoneValue || hardCodedDefaultTimezoneValue,
        field: 'timeZone',
        isFormik: true,
        label: 'Time Zone',
        options: timeZoneSelectionEntries,
        styles: timeZoneCustomSelectStyles,
      },
      {
        id: 2,
        defaultValue: weightTypeDefaultValue,
        disabled: true,
        field: 'weightType',
        isFormik: true,
        label: 'Weight Unit',
        subtext: (
          <>
            Please contact&nbsp;
            <SupportLink onClick={handleOpenSupportModal}>support@teambuildr.com</SupportLink>
            &nbsp;if you would like to update your organization&apos;s preferred weight unit.
          </>
        ),
        options: [
          { label: 'kgs', value: 1 },
          { label: 'lbs', value: 0 },
        ],
        styles: customSelectStyles,
      },
      {
        id: 3,
        defaultValue: maxCalFormulatDefaultValue,
        field: 'maxCalcFormula',
        isFormik: true,
        label: 'Max Calculation Formula',
        options: [
          { label: 'Brzycki', value: 1 },
          { label: 'Epley', value: 2 },
        ],
        styles: customSelectStyles,
      },
      {
        id: 4,
        defaultValue: repCapValue,
        field: 'numberOfReps',
        isFormik: true,
        label: 'Highest # of Reps for Max Calc.',
        options: repsArray,
        styles: customSelectStyles,
      },
      {
        id: 5,
        defaultValue: mapBackendValuesToSelectOptions(
          organizationSettings?.generalSettings?.reportCardRecipients,
          emailReceipeints,
        ),
        field: 'reportCardRecipients',
        isFormik: false,
        label: 'Scheduled Emails Will Be Sent To',
        options: emailReceipeints,
        styles: customSelectStyles,
      },
      {
        id: 6,
        defaultValue: reportCardAssignedCompletedOptions.find((option) => option.label === organizationSettings?.generalSettings?.reportCardDateFilter) || reportCardAssignedCompletedOptions[0],
        field: 'reportCardDateFilter',
        isFormik: false,
        label: 'Performance Assessment Data Date Source',
        subtext: 'Choose the presentation of Performance Assessment data metrics to be calculated against assigned date or completed date.',
        infoIcon: 'Assigned date - better if on-time/scheduled completion is important.\nCompleted date - better if you assign Free Form Programs where the date is less important.',
        options: reportCardAssignedCompletedOptions,
        styles: customSelectStyles,
      },
    ],
    [
      customSelectStyles,
      defaultTimezoneValue,
      handleOpenSupportModal,
      hardCodedDefaultTimezoneValue,
      maxCalFormulatDefaultValue,
      organizationSettings,
      repCapValue,
      repsArray,
      timeZoneCustomSelectStyles,
      timeZoneSelectionEntries,
      weightTypeDefaultValue,
    ],
  );

  const handleDropdownChange = (field, v) => {
    debouncedOrganizationPreferencesSettings({
      ...organizationPreferencesSettings,
      [field]: v,
    });
  };

  const handleResize = (rect) => {
    setContainerWidth(rect.width);
  };

  const handleSwitches = (field, v) => {
    setOrganizationPreferencesSettings((prev) => ({
      ...prev,
      [field]: v,
    }));
    debouncedOrganizationPreferencesSettings({
      ...organizationPreferencesSettings,
      [field]: v,
    });
  };

  const handleFormSubmit = (values, { setSubmitting }) => {
    // When button submits form and form is in the process of submitting,
    // submit button is disabled
    setSubmitting(true);

    const valueObj = {
      generalSettings: {
        maxCalculation: values.maxCalcFormula,
        repCap: parseInt(values.numberOfReps, 10),
        reportCardRecipients: values.emailReceipeints,
        timeZone: values.timeZone,
        weightType: values.weightType,
      },
    };
    // check to see if any values have changed, if nothing has changed, don't hit the API on blur
    if (
      values.timeZone !== currentGlobalTimeZone
      || values.weightType !== currentGlobalWeightType
      || values.maxCalcFormula !== currnetGlobalMaxCalculation
      || values.numberOfReps !== currentGlobalRepCap
    ) {
      dispatch(updateOrganizationSettings(accountCode, valueObj));
    }
    setSubmitting(false);
  };

  const handleSelectChange = (field, value, setFieldValue, handleSubmit) => {
    setFieldValue(field, value);
    handleSubmit();
  };

  const handleToggleChange = (field) => {
    handleSwitches(field, !organizationPreferencesSettings[field]);
  };

  const renderFormField = (fieldConfig, setFieldValue = null, handleSubmit = null) => {
    const onChange = fieldConfig.isFormik && setFieldValue && handleSubmit
      ? (options) => {
        handleSelectChange(fieldConfig.field, options.value, setFieldValue, handleSubmit);
      }
      : (options) => handleDropdownChange(fieldConfig.field, options.value);

    return (
      <Row key={fieldConfig.id} disabled={fieldConfig.disabled}>
        <TextContainer>
          <FormFieldLabelContainer>
            <Text styledTextProps={formFieldTextStyles}>{fieldConfig.label}</Text>
            {fieldConfig.infoIcon ? (
              <FormFieldInfoIcon>
                <Tooltip
                  id={`${fieldConfig.id}-info-tooltip`}
                  place='top'
                  displaySize='12px'
                  displayColor={ColorGray}
                  hoverText={fieldConfig.infoIcon}
                  icon='info'
                  iconSize={12}
                  styledPropsWrapper={{
                    height: '12px',
                    width: '12px',
                  }}
                  tooltipStyles={{
                    whiteSpace: 'pre-line',
                    fontSize: '14px',
                    zIndex: 2,
                    backgroundColor: '#ffffff',
                    color: ColorBlack,
                    borderRadius: '4px',
                    padding: '4px 8px',
                    boxShadow: BoxShadow,
                    maxWidth: '480px',
                  }}
                  tooltipBorder={`1px solid ${ColorGray}`}
                  tooltipOpacity={1}
                />
              </FormFieldInfoIcon>
            ) : null}
          </FormFieldLabelContainer>
          {fieldConfig.subtext && <SubText theme={theme}>{fieldConfig.subtext}</SubText>}
        </TextContainer>
        <div>
          <Select
            defaultValue={fieldConfig.defaultValue}
            isDisabled={fieldConfig.disabled}
            onChange={onChange}
            options={fieldConfig.options}
            styles={fieldConfig.styles}
          />
        </div>
      </Row>
    );
  };

  const renderFormFields = (setFieldValue, handleSubmit) => (
    <>
      {formFieldsConfig.filter((fieldConfig) => fieldConfig.isFormik).map((fieldConfig) => renderFormField(fieldConfig, setFieldValue, handleSubmit))}
    </>
  );

  const renderHeader = () => (
    <HeaderContainer>
      <Text
        styledTextProps={{
          fontSize: containerWidth && containerWidth < 450 ? 24 : 28,
          fontWeight: '700',
        }}
      >
        Organization Preferences
      </Text>
      <Text
        styledTextProps={{
          fontSize: 18,
          fontWeight: '400',
        }}
      >
        Manage your organization&apos;s global preferences.
      </Text>
    </HeaderContainer>
  );

  const renderToggleRow = (label, field, subtext = '') => (
    <Row height={subtext ? 'auto' : '46px'}>
      <TextContainer>
        <Text styledTextProps={formFieldTextStyles}>{label}</Text>
        {subtext && <SubText theme={theme}>{subtext}</SubText>}
      </TextContainer>

      <Toggle
        width='auto'
        defaultChecked={organizationPreferencesSettings[field]}
        onChange={() => handleToggleChange(field)}
      />
    </Row>
  );

  return (
    <Wrapper theme={theme}>
      {renderHeader()}
      <Formik
        initialValues={{
          emailReceipeints: mapRecipientToInteger(
            organizationSettings?.generalSettings?.reportCardRecipients,
          ),
          maxCalcFormula: organizationSettings?.generalSettings?.maxCalculation,
          numberOfReps: organizationSettings?.generalSettings?.repCap,
          timeZone: organizationSettings?.generalSettings?.timeZone,
          weightType: organizationSettings?.generalSettings?.weightType,
        }}
        onSubmit={handleFormSubmit}
      >
        {/* Callback function containing Formik state and
          helpers that handle common form actions... destructured from props */}
        {({ setFieldValue, handleSubmit }) => (
          <form onBlur={handleSubmit}>
            <ResizeObserver onResize={handleResize} />
            {renderFormFields(setFieldValue, handleSubmit)}
          </form>
        )}
      </Formik>
      {renderToggleRow(
        'Athlete Management of Maxes/PRs',
        'athleteManagementOfMaxes',
      )}
      {renderToggleRow('Daily Workout Emails', 'dailyWorkoutEmails')}
      {renderFormField(formFieldsConfig.find((fieldConfig) => fieldConfig.id === 6))}
      {accessLevel >= 6
        && renderToggleRow(
          'Schedule Weekly Performance Assessment Emails',
          'scheduledReportCardEmails',
          'When enabled, emails will go out Sunday at 4:15am EST time (this cannot be changed at the moment).',
        )}
      {organizationPreferencesSettings.scheduledReportCardEmails && (
        renderFormField(formFieldsConfig.find((fieldConfig) => fieldConfig.id === 5))
      )}
      {accessLevel >= 6
        && renderToggleRow(
          'Allow Athletes to Reset Programs',
          'allowAthletesToResetPrograms',
        )}
      {isAmsCoreSubscribed && bodyHeatMapFlag
        && renderToggleRow(
          'Allow Athlete Entry for Body Heatmap',
          'athleteBodyHeatMapEntry',
          'When enabled, athletes will be able to enter Body Heatmap data in the Workout Entry.',
        )}
    </Wrapper>
  );
};

OrganizationPreferences.propTypes = {
  flags: PropTypes.instanceOf(Object),
};

OrganizationPreferences.defaultProps = {
  flags: {},
};

export default withLDConsumer()(OrganizationPreferences);
